import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Archive as ArchiveIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import {
  useTable,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
} from '@tanstack/react-table';
import { features } from '../common/tableFeatures';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetDevicesQuery,
  useBulkDeleteDevicesMutation,
  useDeleteDeviceMutation,
  useGetUnverifiedDevicesQuery,
  useVerifyDeviceMutation,
  useRejectDeviceMutation,
  useChangeDevicePasswordsMutation,
} from '../../store/api/deviceApi';
import type { Device } from '../../types/api';
import { useDebounce } from '../../hooks/useDebounce';
import { useColumnVisibility } from '../../hooks/useColumnVisibility';
import ColumnSelector, { COLUMN_PRESETS } from '../common/ColumnSelector';
import {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
} from '../common/DataTable';
import FilterPanel, { DeviceFilters } from './FilterPanel';
import DPSEnrollmentDialog from './DPSEnrollmentDialog';
import DeviceActionsMenu from './DeviceActionsMenu';
import RejectDeviceDialog from './RejectDeviceDialog';
import ArchiveDeviceDialog from './ArchiveDeviceDialog';
import { DEVICE_COLUMN_METADATA, DEVICE_DEFAULT_COLUMNS } from './deviceColumnMetadata';
import { UNVERIFIED_REGISTRATION_STATUS_PARAM } from './registrationStatus';
import { createDeviceColumns } from './deviceColumns';
import errorService from '../../services/errorService';
import { applyDeviceFilters } from '../../utils/deviceFilters';
import { buildDeviceCsvRows, buildDeviceMongoDocs } from '../../utils/deviceExport';
import { exportToCSV, exportToJSON, downloadFile } from '../../utils/dataExport';

interface DeviceListProps {
  groupId?: string;
}

const DeviceList: React.FC<DeviceListProps> = ({ groupId }) => {
  const navigate = useNavigate();
  // The hook, not notistack's standalone enqueueSnackbar export: Vite's dep
  // pre-bundling leaves that one undefined, so calls through it are dropped.
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [tabValue, setTabValue] = useState<'all' | 'unverified'>('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [filters, setFilters] = useState<DeviceFilters>({});
  const [dpsEnrollmentDialogOpen, setDpsEnrollmentDialogOpen] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  const debouncedSearch = useDebounce(globalFilter, 300);

  // Fetch devices with pagination and filters
  const {
    data: devicesData,
    isLoading,
    error,
    refetch,
  } = useGetDevicesQuery({
    page: page + 1, // API uses 1-based pagination
    pageSize,
    search: debouncedSearch,
    groupId,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
    registration_status:
      tabValue === 'all' ? undefined : UNVERIFIED_REGISTRATION_STATUS_PARAM,
  });

  // Fetch unverified devices count
  const { data: unverifiedDevices } = useGetUnverifiedDevicesQuery();
  const unverifiedCount = unverifiedDevices?.length || 0;

  const [bulkDeleteDevices, { isLoading: isArchiving }] = useBulkDeleteDevicesMutation();
  const [deleteDevice, { isLoading: isArchivingSingle }] = useDeleteDeviceMutation();
  const [verifyDevice, { isLoading: isVerifying }] = useVerifyDeviceMutation();
  const [rejectDevice, { isLoading: isRejecting }] = useRejectDeviceMutation();
  const [changeDevicePasswords, { isLoading: isChangingPasswords }] = useChangeDevicePasswordsMutation();

  // Use column visibility hook
  const {
    visibleColumns,
    toggleColumn,
    resetToDefault,
    applyPreset,
    columnVisibility,
  } = useColumnVisibility({
    columns: DEVICE_COLUMN_METADATA,
    // Bumped when the defaults change, so users pick up the new set instead of
    // staying on a saved one they never chose.
    storageKey: 'deviceList.columnVisibility.v2',
    defaultColumns: DEVICE_DEFAULT_COLUMNS,
    presets: COLUMN_PRESETS,
  });

  // Column definitions. Cell renderers close over navigation and the actions menu.
  const columns = useMemo(
    () =>
      createDeviceColumns({
        onOpenDevice: (deviceId) => navigate(`/devices/${deviceId}`),
        onOpenActions: (event, device) => {
          setActionsAnchor(event.currentTarget);
          setSelectedDevice(device);
        },
      }),
    [navigate]
  );

  // Apply the client-side filters on top of the server-filtered data.
  const filteredData = useMemo(
    () => applyDeviceFilters(devicesData?.data || [], filters),
    [devicesData?.data, filters]
  );

  const table = useTable({
    features,
    data: filteredData,
    columns,
    pageCount: Math.ceil((devicesData?.total || 0) / pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: page,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    // Key selection by device id (not row index) so it stays correct across
    // server-paginated pages instead of leaking onto same-indexed rows.
    getRowId: (row) => row.id,
    manualPagination: true, // Server paginates via API; don't slice rows again client-side
    manualSorting: false,
  });

  // Handle bulk archive. The endpoint is named /bulk/delete but soft-archives
  // unless ?permanent=true, which this never sends.
  const handleBulkArchive = async () => {
    const deviceIds = table.getSelectedRowModel().rows.map(row => row.original.deviceId);
    if (deviceIds.length === 0) return;

    if (window.confirm(`Are you sure you want to archive ${deviceIds.length} device(s)?`)) {
      try {
        const result = await bulkDeleteDevices(deviceIds).unwrap();
        setRowSelection({});
        refetch();
        const archived = result.archived.length;
        enqueueSnackbar(`Archived ${archived} device${archived === 1 ? '' : 's'}`, {
          variant: 'success',
        });
        if (result.not_found.length > 0) {
          enqueueSnackbar(`Not found: ${result.not_found.join(', ')}`, { variant: 'warning' });
        }
      } catch (error) {
        console.error('Failed to archive devices:', error);
        enqueueSnackbar(errorService.toUserMessage(error), { variant: 'error' });
      }
    }
  };

  // Handle export (selected rows, or all rows if none selected)
  const handleExport = (format: 'csv' | 'json') => {
    const selectedRows = table.getSelectedRowModel().rows;
    const rowsToExport = selectedRows.length > 0 ? selectedRows : table.getRowModel().rows;
    const devices = rowsToExport.map(row => row.original);
    const filename = `devices-${new Date().toISOString()}`;

    if (format === 'json') {
      downloadFile(
        exportToJSON(buildDeviceMongoDocs(devices)),
        `${filename}.json`,
        'application/json',
      );
      return;
    }

    downloadFile(
      exportToCSV(buildDeviceCsvRows(devices)),
      `${filename}.csv`,
      'text/csv;charset=utf-8;',
    );
  };

  // Device verification (from the actions menu)
  const handleVerifyDevice = async (device: Device) => {
    try {
      const result = await verifyDevice(device.deviceId).unwrap();
      refetch();
      // The request can succeed while the device itself fails to verify.
      const failure = result.results.find((r) => !r.success);
      if (failure) {
        enqueueSnackbar(failure.message || `Failed to verify ${device.deviceId}`, {
          variant: 'error',
        });
        return;
      }
      enqueueSnackbar(`Device ${device.deviceId} verified`, { variant: 'success' });
    } catch (err) {
      console.error('Failed to verify device:', err);
      enqueueSnackbar(errorService.toUserMessage(err), { variant: 'error' });
    }
  };

  const handleChangePasswords = async (device: Device) => {
    try {
      await changeDevicePasswords(device.deviceId).unwrap();
      enqueueSnackbar(`Password change queued for ${device.deviceId}`, { variant: 'success' });
    } catch (err) {
      console.error('Failed to change device passwords:', err);
      enqueueSnackbar(errorService.toUserMessage(err), { variant: 'error' });
    }
  };

  const handleRejectDevice = async (reason: string) => {
    if (!selectedDevice) return;
    const { deviceId } = selectedDevice;
    try {
      await rejectDevice({ deviceId, reason }).unwrap();
      setRejectDialogOpen(false);
      setSelectedDevice(null);
      refetch();
      enqueueSnackbar(`Device ${deviceId} rejected`, { variant: 'success' });
    } catch (err) {
      console.error('Failed to reject device:', err);
      enqueueSnackbar(errorService.toUserMessage(err), { variant: 'error' });
    }
  };

  const handleArchiveDevice = async () => {
    if (!selectedDevice) return;
    const { deviceId } = selectedDevice;
    try {
      await deleteDevice(deviceId).unwrap();
      setArchiveDialogOpen(false);
      setSelectedDevice(null);
      refetch();
      enqueueSnackbar(`Device ${deviceId} archived`, { variant: 'success' });
    } catch (err) {
      console.error('Failed to archive device:', err);
      enqueueSnackbar(errorService.toUserMessage(err), { variant: 'error' });
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load devices. Please try again later.
      </Alert>
    );
  }

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <Box>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => {
            setTabValue(newValue);
            setPage(0);
          }}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            px: 1,
            '& .MuiTab-root': { minHeight: 40, py: 0, textTransform: 'none', fontSize: '0.875rem' },
          }}
        >
          <Tab label="All Devices" value="all" />
          <Tab
            label={
              <Badge badgeContent={unverifiedCount} color="warning">
                Unverified Devices
              </Badge>
            }
            value="unverified"
          />
        </Tabs>
        {/* No title: the page already carries a "Device Management" heading and
            an "All Devices" tab, so the toolbar holds only the controls. */}
        <DataTableToolbar
          selectedCount={selectedCount}
          searchValue={globalFilter}
          onSearchChange={setGlobalFilter}
          searchPlaceholder="Search devices..."
          searchAriaLabel="Search devices"
          selectedActions={
            <>
              <Tooltip title="Enroll in Azure DPS">
                <IconButton
                  onClick={() => setDpsEnrollmentDialogOpen(true)}
                  aria-label="Enroll selected devices in Azure DPS"
                  color="primary"
                >
                  <CloudIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive">
                <IconButton onClick={handleBulkArchive} disabled={isArchiving} aria-label="Archive selected devices">
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Selected">
                <IconButton
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  aria-label="Export selected devices"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </>
          }
          actions={
            <>
              <ColumnSelector
                columns={DEVICE_COLUMN_METADATA}
                visibleColumns={visibleColumns}
                onColumnToggle={toggleColumn}
                onReset={resetToDefault}
                onPresetSelect={applyPreset}
              />
              <Tooltip title="Export">
                <IconButton
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  aria-label="Export devices"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </>
          }
        />

        <FilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          availableVersions={[]} // TODO: Get from API
          latestVersion={undefined} // TODO: Get from API
        />

        <DataTable
          table={table}
          ariaLabel="Devices"
          isLoading={isLoading}
          emptyMessage="No devices found"
        />

        <DataTablePagination
          count={devicesData?.total || 0}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      </Paper>

      {/* Export Format Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            handleExport('json');
            setExportMenuAnchor(null);
          }}
        >
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Export as JSON (MongoDB Collection)
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExport('csv');
            setExportMenuAnchor(null);
          }}
        >
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Export as CSV
        </MenuItem>
      </Menu>

      <DeviceActionsMenu
        anchorEl={actionsAnchor}
        device={selectedDevice}
        isVerifying={isVerifying}
        isRejecting={isRejecting}
        isChangingPasswords={isChangingPasswords}
        onClose={() => setActionsAnchor(null)}
        onViewDetails={(device) => navigate(`/devices/${device.deviceId}`)}
        onVerify={handleVerifyDevice}
        onReject={() => setRejectDialogOpen(true)}
        onChangePasswords={handleChangePasswords}
        onArchive={() => setArchiveDialogOpen(true)}
      />

      <RejectDeviceDialog
        open={rejectDialogOpen}
        deviceName={selectedDevice?.name}
        isRejecting={isRejecting}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectDevice}
      />

      <ArchiveDeviceDialog
        open={archiveDialogOpen}
        deviceName={selectedDevice?.name}
        isArchiving={isArchivingSingle}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveDevice}
      />

      <DPSEnrollmentDialog
        open={dpsEnrollmentDialogOpen}
        onClose={() => setDpsEnrollmentDialogOpen(false)}
        selectedDevices={table.getSelectedRowModel().rows.map(row => row.original as Device)}
        onSuccess={() => {
          // Refetch devices to get updated DPS status
          refetch();
          // Clear selection
          setRowSelection({});
        }}
      />
    </Box>
  );
};

export default DeviceList;
